import React, { useState } from 'react';

// Bible version map for mapping names to Bible version IDs
const bibleVersionMap = {
  "American Standard Version (Byzantine Text with Apocrypha)": "685d1470fe4d5c3b-01",
  "Brenton English Septuagint (Updated Spelling and Formatting)": "6bab4d6c61b31b80-01",
  "Brenton English translation of the Septuagint": "65bfdebd704a8324-01",
  "Cambridge Paragraph Bible of the KJV": "55212e3cf5d04d49-01",
  "English Majority Text Version": "55ec700d9e0d77ea-01",
  "Free Bible Version": "65eec8e0b60e656b-01",
  "Geneva Bible": "c315fa9f71d4af3a-01",
  "JPS TaNaKH 1917": "bf8f1c7f3f9045a5-01",
  "King James (Authorised) Version": "de4e12af7f28f599-01",
  "Literal Standard Version": "01b29f4b342acc35-01",
  "Revised Version 1885": "40072c4a5aba4022-01",
  "Targum Onkelos Etheridge": "ec290b5045ff54a5-01",
  "The English New Testament According to Family 35": "2f0fd81d7b85b923-01",
  "The Holy Bible, American Standard Version": "06125adad2d5898a-01",
  "The Orthodox Jewish Bible": "c89622d31b60c444-02",
  "The Text-Critical English New Testament": "32339cf2f720ff8e-01",
  "Translation for Translators": "66c22495370cdfc0-01",
  "World English Bible": "9879dbb7cfe39e4d-01",
  "World English Bible Updated": "72f4e6dc683324df-01",
  "World Messianic Bible": "f72b840c855f362c-04",
};

const CompareBibles = () => {
  const [verses, setVerses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Function to fetch verses from the API.Bible
  const fetchVersesFromBibleAPI = async (verseReference) => {
    try {
      setLoading(true);
      const promises = Object.entries(bibleVersionMap).map(([versionName, versionId]) =>
        fetch(
          `https://api.scripture.api.bible/v1/bibles/${versionId}/passages?reference=${encodeURIComponent(
            verseReference
          )}`,
          {
            headers: {
              'api-key': process.env.REACT_APP_BIBLE_API_KEY,
            },
          }
        )
          .then((response) => {
            if (!response.ok) {
              // If the response has a status code of 400 or above, throw an error
              throw new Error(`Error: ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            if (data.data && data.data[0].content) {
              let content = data.data[0].content.replace('</span>', '</span>. ')
              return {
                version: versionName,
                content: content,
                reference: verseReference,
              };
            } else {
              return {
                version: versionName,
                content: 'No verse available',
                reference: verseReference,
              };
            }
          })
          .catch((error) => {
            if (error.message.includes('400')) {
              // Handle the specific 400 error
              return {
                version: versionName,
                content: 'No verse available',
                reference: verseReference,
              };
            }
            throw error;
          })
      );

      const results = await Promise.all(promises);
      setVerses(results);
    } catch (error) {
      setError('Failed to fetch the verse. Please try again.');
      console.error('Error fetching verses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle the search submission
  const handleSearch = async (event) => {
    event.preventDefault();
    if (searchTerm.trim() !== '') {
      await fetchVersesFromBibleAPI(searchTerm);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-100">
      {/* Introductory Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-blue-700 mb-2">Compare Bible Translations</h1>
        <p className="text-gray-800 text-base leading-relaxed">
          The Word of God has been translated into many languages and styles. Each Bible version reflects
          unique choices in language, theology, and clarity. This tool helps you explore how different
          translations render the same verse so you can deepen your understanding and gain new insights
          into Scripture.
        </p>
      </div>

      {/* Quick Guide */}
      <div className="mb-6 bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold mb-2">How to Use This Tool</h2>
        <ul className="list-disc list-inside text-gray-700">
          <li>Type a Bible reference (e.g., <strong>John 1:1</strong>) in the box below</li>
          <li>Click <strong>Search</strong> to view the verse in multiple translations</li>
          <li>Read, reflect, and observe the wording, tone, and meaning differences</li>
        </ul>
      </div>

      {/* Benefits of Comparison */}
      <div className="mb-6 bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold mb-2">Why Compare Bible Versions?</h2>
        <p className="text-gray-700">
          No single English version can perfectly capture the richness of the original Hebrew and Greek texts.
          Some translations are more literal (like the NASB or ESV), while others aim for readability (like the NLT or GNT).
          Comparing versions side-by-side can help:
        </p>
        <ul className="list-disc list-inside mt-2 text-gray-700">
          <li>Clarify difficult or debated passages</li>
          <li>Discover deeper theological themes</li>
          <li>Find devotional insight through diverse expressions</li>
        </ul>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <input
          type="text"
          className="border p-2 w-full rounded"
          placeholder="Enter verse (e.g., Genesis 1:1)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button type="submit" className="bg-blue-500 text-white p-2 rounded mt-2">
          Search
        </button>
      </form>

      {/* Loading Indicator */}
      {loading && <p>Loading...</p>}

      {/* Error Message */}
      {error && <p className="text-red-500">{error}</p>}

      {/* Verse Results */}
      <div>
        {verses.length > 0 ? (
          verses.map((verse, index) => (
            <div key={index} className="p-4 mb-4 bg-white rounded-lg shadow">
              <h2 className="text-xl font-semibold text-blue-600">{verse.version}</h2>
              <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: verse.content }} />
              <span className="text-sm text-gray-500">{verse.reference}</span>
            </div>
          ))
        ) : (
          !loading && <p>No verses found. Please enter a verse to compare translations.</p>
        )}
      </div>

      {/* Footer Encouragement */}
      <div className="mt-10 text-sm text-center text-gray-600">
        <p>
          “All Scripture is God-breathed and is useful for teaching, rebuking, correcting and training in righteousness.” — 2 Timothy 3:16
        </p>
        <p className="mt-2">We pray this tool strengthens your faith and enriches your Bible study.</p>
      </div>
    </div>

  );
};

export default CompareBibles;
